import React, { useState } from "react";
import { Box, useTheme, useMediaQuery, Grid, Typography } from "@mui/material";
import { IconButton, Divider } from "@mui/material";
import { usePostHog } from "posthog-js/react";
import { CLICK_SOURCE_FULL_SCREEN } from "../../../../../../utils/posthogEvents";
import PDFView from "../../../../../../components/PDFView";
import FullViewPDF from "../../../components/FullViewPDF";
import { CurrentPagePlugin } from "../../../../../../components/PDFView/Toolbar";
import { Fullscreen, CloseRounded } from "@mui/icons-material";
import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { zoomPlugin } from "@react-pdf-viewer/zoom";
import { printPlugin } from "@react-pdf-viewer/print";
import { getFilePlugin } from "@react-pdf-viewer/get-file";
import "@react-pdf-viewer/print/lib/styles/index.css";
import PDFViewWithMultiplePagesHighlighted from "../../../../../../components/PDFView/PDFViewWithMultiplePagesHighlighted";
import CSVViewer from "../../../../../ViewFile/CSVViewer";
import { makeStyles } from "tss-react/mui";

const useStyles = makeStyles()((theme) => ({
  iconButton: {
    color: theme.palette.text.primary,
  },
}));

function RelatedDocDialog({ relatedDoc, onClose }) {
  const { classes } = useStyles();
  const { chunk_content, citations, section_title, file_name, file_url } =
    relatedDoc;
  const theme = useTheme();
  const [isFullViewDialogOpen, setIsFullViewDialogOpen] = useState(false);
  const isXS = useMediaQuery(theme.breakpoints.down("sm"));
  const zoomPluginInstance1 = zoomPlugin();
  const pageNavigationPluginInstance1 = pageNavigationPlugin();
  const zoomPluginInstance2 = zoomPlugin();
  const pageNavigationPluginInstance2 = pageNavigationPlugin();
  const printPluginInstance = printPlugin();
  const getFilePluginInstance = getFilePlugin();
  const posthog = usePostHog();
  const isCSV =
    citations.length > 0 &&
    citations[0]["sheet"] &&
    citations[0]["sheet"]["csv_url"];

  const handleOpenDialog = () => {
    posthog.capture(CLICK_SOURCE_FULL_SCREEN);
    setIsFullViewDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsFullViewDialogOpen(false);
  };

  return (
    <Grid
      item
      size={{ md: 5 }}
      height={"100%"}
      sx={{
        borderLeft: `1px solid ${theme.palette.divider}`,
        display: "flex",
        flexDirection: "column",
      }}>
      <Box
        px={2}
        py={1}
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
        gap={1}>
        <Box
          display={"flex"}
          alignItems={"center"}
          gap={1}
          sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body1"
            noWrap
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: 1.2,
              fontWeight: 600,
            }}>
            {section_title}
          </Typography>
        </Box>
        <Box display={"flex"} alignItems={"center"}>
          {file_url && (
            <>
              {!isCSV && (
                <CurrentPagePlugin
                  pageNavigationPluginInstance={pageNavigationPluginInstance1}
                />
              )}
              <IconButton className={classes.iconButton} onClick={handleOpenDialog}>
                <Fullscreen />
              </IconButton>
              <Divider orientation="vertical" sx={{ mx: 1, height: 24 }} />
            </>
          )}
          <IconButton className={classes.iconButton} onClick={onClose}>
            <CloseRounded />
          </IconButton>
        </Box>
      </Box>
      <Divider />
      <Box px={2} py={1}>
        <Typography variant="body2" noWrap>
          {file_name}
        </Typography>
      </Box>
      <Divider />
      <Box
        px={1}
        bgcolor={"#525659"}
        sx={{
          flex: 1,
          overflow: "auto",
          cursor: "pointer",
        }}>
        {isCSV ? (
          <CSVViewer file={{ file_url: citations[0]["sheet"]["csv_url"] }} />
        ) : file_url ? (
          <PDFViewWithMultiplePagesHighlighted
            citations={citations}
            fileUrl={file_url}
            scale={isXS ? 0.5 : 0.8}
            zoomPluginInstance={zoomPluginInstance1}
            pageNavigationPluginInstance={pageNavigationPluginInstance1}
            printPluginInstance={printPluginInstance}
            getFilePluginInstance={getFilePluginInstance}
          />
        ) : (
          <Typography sx={{ color: "white" }}>
            Error while loading the document
          </Typography>
        )}
      </Box>
      {isFullViewDialogOpen && (
        <FullViewPDF
          isCSV={isCSV}
          csvFileUrl={citations[0]?.["sheet"]?.["csv_url"]}
          zoomPluginInstance={zoomPluginInstance2}
          pageNavigationPluginInstance={pageNavigationPluginInstance2}
          relatedDoc={relatedDoc}
          printPluginInstance={printPluginInstance}
          onClose={handleCloseDialog}
        />
      )}
    </Grid>
  );
}

export default RelatedDocDialog;
